import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { PairLink } from '@root/entities/pair/ui/common'
import { useTranslation } from '@root/shared/localization'
import {
  FadeOverflow,
  type Column,
  type DataTableModel,
} from '@root/shared/ui-kit'

import { ExploreColumn } from '../../../explore-table/explore-column'

import type { ExploreTableColumnId } from '../../../explore-table/explore-column'
import type { ExploreTableContext } from '../../../explore-table/explore-table-types'
import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

// TODO: Rename this module, it's not about filters

const LIST_FOOTER_COLUMNS = [
  [ExploreColumn.MarketCap],
  [ExploreColumn.Volume],
  [ExploreColumn.Holders],
  [ExploreColumn.Swaps],
  [ExploreColumn.Audit],
]

type ExploreListItemProps = {} & ExploreListItemCommonProps

type ExploreListItemCommonProps = {
  pair: PairModel
  index: number
  model: DataTableModel<
    PairModel,
    ExploreTableColumnId,
    object,
    ExploreTableContext
  >
  context: ExploreTableContext
} & ClassNameProps

export const ExploreListItem: React.FC<ExploreListItemProps> = (props) => {
  return (
    <Root $index={props.index} className={props.className}>
      <PairExploreBackgroundLink pair={props.pair} />
      <Header>
        {ExploreColumn.Currency.render({
          value: props.pair,
          index: props.index,
          model: props.model,
          context: props.context,
          column: ExploreColumn.Currency,
        })}

        {ExploreColumn.Buy.render({
          value: props.pair,
          index: props.index,
          model: props.model,
          context: props.context,
          column: ExploreColumn.Currency,
        })}
      </Header>
      <Footer variant="horizontal">
        {LIST_FOOTER_COLUMNS.map((columns, index) => (
          <ExploreFilterItemCell
            key={index}
            columns={columns}
            context={props.context}
            index={props.index}
            model={props.model}
            pair={props.pair}
          />
        ))}
      </Footer>
    </Root>
  )
}

const Root = styled.div<{ $index: number }>`
  cursor: pointer;
  position: relative;
  background: ${(props) =>
    props.$index % 2 ? 'var(--background-1)' : 'var(--appearance-1)'};
`

const PairExploreBackgroundLink = styled(PairLink<PairModel>)`
  position: absolute;
  inset: 0;
  z-index: 0;
`

const Header = styled.div`
  pointer-events: none;
  display: grid;
  position: relative;
  grid-template-columns: 1fr auto;
  padding-inline: 12px;
  gap: 6px;
  border-bottom: 1px solid var(--backgroundA-3);

  height: 60px;
  align-items: center;
`

const Footer = styled(FadeOverflow)`
  pointer-events: none;
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  padding-inline: 12px;
  justify-content: space-between;
  border-bottom: 1px solid var(--backgroundA-3);
  overflow: auto;

  height: 64px;

  padding-top: 9px;
`

type ExploreFilterItemCellProps = ExploreListItemCommonProps & {
  columns: Array<Column<PairModel, ExploreTableColumnId, ExploreTableContext>>
}

const ExploreFilterItemCell = (props: ExploreFilterItemCellProps) => {
  const { t } = useTranslation()

  return (
    <ExploreFilterItemCellGroup>
      {props.columns
        .filter((column) => !column.hidden)
        .map((col) => (
          <ExploreFilterItemCellRoot key={col.id} className={props.className}>
            <ExploreFilterItemCellHeader>
              {typeof col.header === 'string'
                ? col.header
                : col.header({
                    columnId: col.id,
                    context: props.context,
                    clearColumnFilter: () => {},
                    filter: undefined,
                    setColumnFilter: () => {},
                    t,
                    itemsStateAtom: props.model,
                    style: {
                      minWidth: col.minWidth,
                      maxWidth: col.maxWidth,
                    },
                  })}
            </ExploreFilterItemCellHeader>
            <ExploreFilterItemCellContent>
              {col.render({
                value: props.pair,
                index: props.index,
                model: props.model,
                context: props.context,
                column: ExploreColumn.Currency,
              })}
            </ExploreFilterItemCellContent>
          </ExploreFilterItemCellRoot>
        ))}
    </ExploreFilterItemCellGroup>
  )
}

const ExploreFilterItemCellGroup = styled.div`
  display: grid;
`

const ExploreFilterItemCellRoot = styled.div``
const ExploreFilterItemCellHeader = styled(Typography).attrs({
  variant: 'body5-medium',
  component: 'div',
})`
  color: var(--textA-8);
  margin-bottom: 3px;
`
const ExploreFilterItemCellContent = styled.div``
